import { useEffect, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import {
  ILoyaltyServiceAnonymousOfferQuery,
  ILoyaltyServiceAnonymousOfferQueryVariables,
  ILoyaltyServiceOfferQuery,
  ILoyaltyServiceOfferQueryVariables,
  LoyaltyServiceAnonymousOfferDocument,
  LoyaltyServiceOfferDocument,
  LoyaltyServiceOfferRedemptionType,
} from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import { logger } from 'utils/logger';

import { ServiceOffer } from '../types';
import { mapServiceOffer } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

interface UseLoyaltyServiceAppliedOfferRecoveryOptions {
  enabled: boolean;
  loadingLoyaltyUser: boolean;
  loyaltyUserId?: string;
}

export function useLoyaltyServiceAppliedOfferRecovery({
  enabled,
  loadingLoyaltyUser,
  loyaltyUserId,
}: UseLoyaltyServiceAppliedOfferRecoveryOptions) {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);

  const missingAppliedOfferIds = useMemo(() => {
    const cachedOfferIds = new Set(cmsOffers.map(offer => offer.loyaltyEngineId));

    return [...new Set(appliedOffers.flatMap(offer => (offer.id ? [offer.id] : [])))].filter(
      offerId => !cachedOfferIds.has(offerId)
    );
  }, [appliedOffers, cmsOffers]);

  useEffect(() => {
    if (!enabled || loadingLoyaltyUser || !storeId || !missingAppliedOfferIds.length) {
      return;
    }

    let isCurrent = true;
    const commonVariables = {
      serviceMode: getLoyaltyServiceServiceMode(serviceMode),
      storeId,
      redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
      includeOfferGroups: true,
      omitInvalids: false,
    };

    const recoverAppliedOffers = async () => {
      const results = await Promise.allSettled(
        missingAppliedOfferIds.map(async id => {
          if (loyaltyUserId) {
            const { data } = await client.query<
              ILoyaltyServiceOfferQuery,
              ILoyaltyServiceOfferQueryVariables
            >({
              query: LoyaltyServiceOfferDocument,
              variables: { ...commonVariables, id },
              fetchPolicy: 'network-only',
              errorPolicy: 'all',
            });
            return data.loyaltyServiceOffer;
          }

          const { data } = await client.query<
            ILoyaltyServiceAnonymousOfferQuery,
            ILoyaltyServiceAnonymousOfferQueryVariables
          >({
            query: LoyaltyServiceAnonymousOfferDocument,
            variables: { ...commonVariables, id },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          });
          return data.loyaltyServiceAnonymousOffer;
        })
      );

      // This function starts synchronously, so a pre-await check cannot prevent these requests.
      // Check after settlement to keep stale effects from writing compatibility state or logging.
      if (!isCurrent) {
        return;
      }

      const recoveredOffers = results.flatMap(result =>
        result.status === 'fulfilled' && result.value ? [result.value] : []
      );

      if (recoveredOffers.length) {
        dispatch(
          actions.loyalty.setCmsOffers(
            recoveredOffers.map(offer =>
              appOfferToLoyaltyOffer(mapServiceOffer(offer as ServiceOffer))
            )
          )
        );
      }

      const rejectedOfferIds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [missingAppliedOfferIds[index]] : []
      );
      if (rejectedOfferIds.length) {
        logger.warn({
          message: 'Loyalty-service applied-offer recovery requests failed',
          loyaltyEngineIds: rejectedOfferIds,
        });
      }
    };

    void recoverAppliedOffers();

    return () => {
      isCurrent = false;
    };
  }, [
    client,
    dispatch,
    enabled,
    loadingLoyaltyUser,
    loyaltyUserId,
    missingAppliedOfferIds,
    serviceMode,
    storeId,
  ]);
}
